<template>
  <div>
    <b-row class="justify-content-end">
      <b-col md="10" class="my-1"> </b-col>
      <b-col lg="2">
        <b-button variant="success" class="float-right my-1" @click="openAddTaskModal"><feather-icon
            icon="PlusIcon" />Add Task</b-button>
      </b-col>
    </b-row>

    <div class="d-flex justify-content-center" v-if="loading">
      <b-spinner variant="success" label="Spinning" style="width: 3rem; height: 3rem"></b-spinner>
    </div>

    <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1;">
      <b-button variant="outline-danger" size="sm" @click="clearFilters">
        Clear Filters
      </b-button>
    </b-col>

    <b-row>
      <b-col lg="12">
        <b-table striped hover responsive :items="paginatedTasks" :fields="fields">

          <template #head(status)="scope">
            <div class="d-flex flex-column">
              <span>Status</span>
              <b-form-select
                v-model="filters.status"
                :options=optionStatusFilters
                @change="currentPage = 1"
                size="sm"
              />
            </div>
          </template>
          

          <template #head(title)="scope">
            <div class="d-flex flex-column">
              <span>Title</span>
              <b-form-input v-model="filters.title" placeholder="Search Title" size="sm" @input="currentPage = 1" />
            </div>
          </template>

          <!-- Description Filter -->
          <template #head(description)="scope">
            <div class="d-flex flex-column">
              <span>Description</span>
              <b-form-input v-model="filters.description" placeholder="Search Description" size="sm"
                @input="currentPage = 1" />
            </div>
          </template>

          <!-- Sales Person Filter -->
          <template #head(salesPerson)="scope">
            <div class="d-flex flex-column">
              <span>Sales Person</span>
              <b-form-input v-model="filters.salesPerson" placeholder="Search Sales Person" size="sm"
                @input="currentPage = 1" />
            </div>
          </template>

          <!-- Tag Filter -->
          <template #head(importance)="scope">
            <div class="d-flex flex-column">
              <span>Tag</span>
              <b-form-select v-model="filters.importance" :options="[
                { value: null, text: 'All' },
                { value: true, text: 'Important' },
                { value: false, text: 'Mild' },
              ]" @change="currentPage = 1" size="sm" />
            </div>
          </template>

          <!-- Due Date Filter -->
          <template #head(dueDate)="scope">
            <div class="d-flex flex-column">
              <span>Due Date</span>
              <DatePicker v-model="filters.dueDateRange" range format="YYYY-MM-DD" placeholder="Select date range"
                @change="currentPage = 1" style="width: 100%;" size="sm" />
            </div>
          </template>

          <!-- Created On Filter -->
          <template #head(createdOn)="scope">
            <div class="d-flex flex-column">
              <span>Created On</span>
              <DatePicker v-model="filters.createdOnRange" range format="YYYY-MM-DD" placeholder="Select date range"
                @change="currentPage = 1" style="width: 100%;" size="sm" />
            </div>
          </template>
          <!-- Title -->
          <template #cell(title)="data">
            <h5>{{ data.item.title || "-N/A-" }}</h5>
          </template>

          <template #cell(description)="data">
            <h5>{{ data.item.description || "no description" }}</h5>
          </template>

          <!-- Sales Person -->
          <template #cell(salesPerson)="data">
            <h5>{{ getSalePersonName(data.item.salesPersonId) || "-N/A-" }}</h5>
          </template>

          <!-- Due Date -->
          <template #cell(dueDate)="data">
            <h6>
              {{ formatDate(data.item.dueDate) || "-N/A-" }}
            </h6>
          </template>

          <!-- Importance -->
          <template #cell(importance)="data">
            <b-badge variant="bg-transparent" class="d-flex">
              <feather-icon icon="CircleIcon" :class="['mr-1 text-' + getImportanceVariant(data.item.isImportant)]"
                size="20" />
              <h5>{{ data.item.isImportant ? "Important" : "Mild" }}</h5>
            </b-badge>
          </template>

          <!-- Created On -->
          <template #cell(createdOn)="data">
            <h6>
              {{ formatDate(data.item.created_On) || "-N/A-" }}
            </h6>
          </template>

          <!-- Actions -->
          <template #cell(actions)="data">
            <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
              <template #button-content>
                <feather-icon icon="MoreHorizontalIcon" size="16" class="text-body align-middle mr-25" />
              </template>

              <b-dropdown-item @click="openEditModal(data.item)">
                <feather-icon icon="EditIcon" class="mr-50" />
                <span>View / Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDelete(data.item)">
                <feather-icon icon="Trash2Icon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col>
        <b-pagination v-model="currentPage" :per-page="perPage" :total-rows="totalRows" align="center"
          aria-controls="table"></b-pagination>
      </b-col>
    </b-row>

    <!--- Add Task Modal -->
    <b-modal id="addTaskModal" title="Add Task" v-model="showAddTaskModal" centered @ok="handleAddTask" size="lg">
      <b-row>
        <b-col md="6">
          <b-form-group label="Task Title" label-for="TaskTitleInput" :state="titleValidation"
            :invalid-feedback="titleError">
            <b-form-input id="TaskTitleInput" required v-model="title" @change="validateTitle"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Tag" label-for="tagInput">
            <v-select :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="optionTags" label="title"
              v-model="selectedTag" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Sales Person" label-for="salesPersonInput" :state="salespersonValidation"
            :invalid-feedback="salespersonError">
            <b-form-select id="salesPersonId" v-model="salesPersonId" :options="optionsSalesPerson" required
              @input="validateSalesperson" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Due Date" label-for="dueDateInput" :state="dueDateValidation"
            :invalid-feedback="dueDateError">
            <b-form-datepicker id="example-purchase" class="mb-1" v-model="dueDate" required @input="validateDueDate" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Status" label-for="status">
            <b-form-select :options="optionStatus" v-model="selectedStatus" />
          </b-form-group>
        </b-col>

        <b-col md="12">
          <b-form-group label="Description" label-for="editDescription">
            <b-form-textarea id="editDescription" v-model="description"></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <b-button @click="handleAddTask">
          <b-spinner v-if="loadingAdd" small></b-spinner>
          <span v-if="!loadingAdd">Add Task</span>
        </b-button>
      </template>
    </b-modal>
    <!-- View Task Modal -->
    <b-modal id="viewModal" title="View Task" v-model="showViewModal" centered>
      <p><strong>Task Title: </strong>{{ selectedTask.title }}</p>

      <p>
        <strong>Sales Person: </strong>{{ getSalePersonName(selectedTask.salesPersonId) }}
      </p>
      <p><strong>Description: </strong>{{ selectedTask.description }}</p>
      <p><strong>Date: </strong>{{ formatDate(selectedTask.dueDate) }}</p>
      <p>
        <strong>Status: </strong><b-badge variant="bg-transparent" class="d-flex">
          <feather-icon icon="CircleIcon" :class="[
            'mr-1 text-' + getImportanceVariant(selectedTask.isImportant),
          ]" size="20" />
          <h5>{{ selectedTask.isImportant ? "Important" : "Mild" }}</h5>
        </b-badge>
      </p>
      <template #modal-footer></template>
    </b-modal>
    <!-- Edit Task Modal -->
    <b-modal id="editTaskModal" title="Add Task" v-model="showEditTaskModal" centered size="lg">
      <b-row>
        <b-col md="6">
          <b-form-group label="Task Title" label-for="TaskTitleInput" :state="editTitleValidation"
            :invalid-feedback="editTitleError">
            <b-form-input id="TaskTitleInput" v-model="selectedTask.title" required
              @change="validateEditTitle"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Tag" label-for="tagInput">
            <v-select :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="optionTags" label="title"
              v-model="selectedTag" :clearable="false" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Sales Person" label-for="salesPersonInput">
            <b-form-select v-model="selectedTask.salesPersonId" :options="optionsSalesPerson" />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Due Date" label-for="dueDateInput" :state="editDueDateValidation"
            :invalid-feedback="editDueDateError">
            <b-form-datepicker id="example-purchase" class="mb-1" v-model="selectedTask.dueDate" required
              @blur="validateEditDueDate"></b-form-datepicker>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Status" label-for="status">
            <b-form-select :options="optionStatus" v-model="selectedTask.status" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Comment" label-for="editDescription">
            <b-form-textarea id="editDescription" v-model="selectedTask.description"></b-form-textarea>
          </b-form-group>
        </b-col>
      </b-row>
      <template #modal-footer>
        <b-button @click="handleEditTask">
          <b-spinner v-if="loading" type="grow" small></b-spinner>
          <span v-if="!loading">Update Task</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  BPagination,
  BListGroup,
  BListGroupItem,
  BFormDatepicker,
  BCard,
  BRow,
  BCol,
  BButton,
  BCardTitle,
  BCardSubTitle,
  BBadge,
  BTable,
  BDropdown,
  BSpinner,
  BDropdownItem,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BInputGroup,
  BInputGroupAppend,
  BForm,
  BFormSelect,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { fnSortingDropdown, customLoader } from "@/libs/customFn";

export default {
  components: {
    BListGroup,
    BListGroupItem,
    BFormDatepicker,
    BSpinner,
    BCard,
    BTable,
    BRow,
    BCol,
    BButton,
    BCardTitle,
    BCardSubTitle,
    BBadge,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BFormInput,
    vSelect,
    flatPickr,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BForm,
    BFormSelect,
    BPagination,
    DatePicker,
  },
  props: {
    heading: {
      type: String,
      default: "To Do",
    },
  },
  data() {
    return {
      titleValidation: null,
      descriptionValidation: null,
      salespersonValidation: null,
      filterOn: [],
      dueDateValidation: null,
      titleError: "",
      descriptionError: "",
      dueDateError: "",
      editTitleValidation: null,
      editDescriptionValidation: null,
      editDueDateValidation: null,
      editTitleError: "",
      editDescriptionError: "",
      editDueDateError: "",
      id: this.$route.params.id,
      loadingAdd: false,
      salespersonError: "",
      salespersonValidation: "",
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      title: "",
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 1,
      description: "",
      selectedStatus: "",
      selectedTag: "",
      salesPersonId: "",
      showAddTaskModal: false,
      showViewModal: false,
      showEditTaskModal: false,
      selectedTask: {},
      dueDate: null,
      optionsSalesPerson: [],
      isImportant: false,
      fields: [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'description', label: 'description', sortable: true },
        { key: 'salesPerson', label: 'Sales Person', sortable: true },
        { key: 'dueDate', label: 'Due Date', sortable: true },
        { key: 'importance', label: 'Tag', sortable: true },
        { key: 'status', label: 'Status', sortable: true },

        { key: 'createdOn', label: 'Created On', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-center' },

      ],
      filters: {
        title: "",
        description: "",
        status: "",
        salesPerson: "",
        dueDateRange: [null, null],
        importance: null,
        createdOnRange: [null, null],
        status: null,
      },
      optionTags: [
        { title: "Mild", value: false },
        { title: "Important", value: true },
      ],
      optionStatus: [
        { value: "NEW", text: "NEW" },
        { value: "INPROGRESS", text: "INPROGRESS" },
        { value: "COMPLETED", text: "COMPLETED" }
      ],
      optionStatusFilters: [
        { value: null, text: "ALL" },
        { value: "NEW", text: "NEW" },
        { value: "INPROGRESS", text: "INPROGRESS" },
        { value: "COMPLETED", text: "COMPLETED" }
      ],
    };
  },

  computed: {
    filteredTasks() {
      return this.sortedTasks.filter((task) => {
        const matchesTitle = !this.filters.title || (task.title && task.title.toLowerCase().includes(this.filters.title.toLowerCase()));
        const matchesDescription = !this.filters.description || (task.description && task.description.toLowerCase().includes(this.filters.description.toLowerCase()));
        const matchesSalesPerson = !this.filters.salesPerson ||
          (this.getSalePersonName(task.salesPersonId) && this.getSalePersonName(task.salesPersonId).toLowerCase().includes(this.filters.salesPerson.toLowerCase()));
        const matchesImportance = this.filters.importance === null || task.isImportant === this.filters.importance;
        const matchesStatus = !this.filters.status || task.status === this.filters.status; // Add this line
        const matchesDueDate = !this.filters.dueDateRange[0] || !this.filters.dueDateRange[1] ||
          (new Date(task.dueDate) >= new Date(this.filters.dueDateRange[0]) && new Date(task.dueDate) <= new Date(this.filters.dueDateRange[1]));
        const matchesCreatedOn = !this.filters.createdOnRange[0] || !this.filters.createdOnRange[1] ||
          (new Date(task.created_On) >= new Date(this.filters.createdOnRange[0]) && new Date(task.created_On) <= new Date(this.filters.createdOnRange[1]));

        return (
          matchesTitle &&
          matchesDescription &&
          matchesSalesPerson &&
          matchesImportance &&
          matchesStatus && // Include the status filter
          matchesDueDate &&
          matchesCreatedOn
        );
      });
    },



    paginatedTasks() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredTasks.slice(start, end); // Use filteredTasks here
    },

    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      loadingSales: "loading",
    }),

    ...mapGetters("taskModule", {
      sortedTasks: "taskbyleadid",
      loading: "loading",
    }),

    formattedTasks() {
      return this.sortedTasks.map((task) => ({
        ...task,
        salesPerson: this.getSalePersonName(task.salesPersonId),
      }));
    },
  },
  methods: {
    ...mapActions("salesPersonModule", ["getSalesPersonListAction"]),
    ...mapActions("taskModule", [
      "getTaskListAction",
      "getTaskByLeadIdAction",
      "updateTaskAction",
      "removeTaskAction",
      "addTaskAction",
    ]),
    clearFilters() {
      this.filters = {
        title: "",
        description: "",
        salesPerson: "",
        dueDateRange: [null, null],
        importance: null,
        createdOnRange: [null, null],
      };
      this.currentPage = 1; // Reset to the first page
    },
    formatDate(isoString) {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-indexed in JavaScript
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    onFiltered(filteredItems) {
      // Handle filtered items if needed
    },
    handleAddTask() {
    
      this.addTasks();
   
    
    },
    handleEditTask() {
      this.validateEditTitle();
      this.validateEditDueDate();

      if (this.editTitleValidation && this.editDueDateValidation) {
        this.editTask();
      }
    },

    validateEditTitle() {
      if (!this.selectedTask.title) {
        this.editTitleValidation = false;
        this.editTitleError = "Title is required";
      } else {
        this.editTitleValidation = true;
        this.editTitleError = "";
      }
    },

    validateEditDescription() {
      if (!this.selectedTask.description) {
        this.editDescriptionValidation = false;
        this.editDescriptionError = "Description is required";
      } else {
        this.editDescriptionValidation = true;
        this.editDescriptionError = "";
      }
    },

    validateEditDueDate() {
      if (!this.selectedTask.dueDate) {
        this.editDueDateValidation = false;
        this.editDueDateError = "Due date is required";
      } else {
        this.editDueDateValidation = true;
        this.editDueDateError = "";
      }
    },
    validateTitle() {
      if (!this.title) {
        this.titleValidation = false;
        this.titleError = "Title is required";
      } else {
        this.titleValidation = true;
        this.titleError = "";
      }
    },
    validateSalesperson() {
      if (!this.salesPersonId) {
        this.salespersonValidation = false;
        this.salespersonError = "Salesperson is required";
      } else {
        this.salespersonValidation = true;
        this.salespersonError = "";
      }
    },
    validateDescription() {
      if (!this.description) {
        this.descriptionValidation = false;
        this.descriptionError = "Description is required";
      } else {
        this.descriptionValidation = true;
        this.descriptionError = "";
      }
    },
    validateDueDate() {
      if (!this.dueDate) {
        this.dueDateValidation = false;
        this.dueDateError = "Due date is required";
      } else {
        this.dueDateValidation = true;
        this.dueDateError = "";
      }
    },
    editTask() {
      const payload = {
        id: this.selectedTask.id,
        title: this.selectedTask.title,
        salesPersonId: this.selectedTask.salesPersonId,
        dueDate: this.selectedTask.dueDate,
        status: this.selectedTask.status,
        tag: this.selectedTag.value ? "Important" : "Mild",
        description: this.selectedTask.description,
        isImportant: this.selectedTag.value,
        isActive: this.selectedTask.isActive,
        leadClientId: this.id,
      };

      this.updateTaskAction(payload)
        .then((response) => {
          //this.getTasks(this.id);
          this.$swal({
            icon: "success",
            title: "Success!",
            text: "Task updated successfully.",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          this.showEditTaskModal = false;
        })
        .catch((error) => {
          this.$swal({
            icon: "error",
            title: "Error!",
            text: "Failed to update Task.",
            customClass: {
              confirmButton: "btn btn-warning",
            },
          });
        });
    },

    getImportanceVariant(isImportant) {
      return isImportant ? "danger" : "info";
    },

    getSalePersonName(id) {
      const salesPerson = this.SalesPersonList.find((s) => s.id === id);
      return salesPerson
        ? salesPerson.firstName + " " + salesPerson.lastName
        : "";
    },

    //  getTasks(id) {
    //   //this.loading = true;
    //   this.getTaskByLeadIdAction(id);

    //     // .then((response) => {
    //     //   this.Tasks = response.data.result;
    //     //   console.log("tasksssssssssssssssssssssss", this.Tasks);
    //     //   this.loading = false;
    //     // })
    //     // .catch((error) => {
    //     //   this.loading = false;
    //     // });
    // },

    async addTasks() {
      this.loadingAdd = true;
      this.validateTitle();
      this.validateDueDate();
      this.validateSalesperson();
      if (
        this.titleValidation &&
        this.dueDateValidation &&
        this.salespersonValidation
      ) {
        let payload = {
          title: this.title,
          salesPersonId: this.salesPersonId,
          dueDate: this.dueDate,
          tag: this.isImportant ? "Important" : "Mild",
          status: this.selectedStatus,
          description: this.description,
          isImportant: this.selectedTag.value,
          isActive: true,
          leadClientId: this.id,
        };

        await this.addTaskAction(payload)
          .then((response) => {
            // this.getTasks(this.id);
            this.$swal({
              icon: "success",
              title: "Success!",
              text: "Task added successfully.",
              customClass: {
                confirmButton: "btn btn-success",
              },
            });

            this.title = "";
            this.salesPersonId = "";
            this.dueDate = null;
            this.description = "";
            this.selectedTag = "";
            this.isImportant = false;
            this.showAddTaskModal = false;
          })
          .catch((error) => {
            this.loadingAdd = false;
            this.$swal({
              icon: "error",
              title: "Error!",
              text: "Failed to add Task.",
              customClass: {
                confirmButton: "btn btn-success",
              },
            });
          });

        await this.getTaskByLeadIdAction(this.id);
        this.loadingAdd = false;
      }
    },
    openAddTaskModal() {
      this.showAddTaskModal = true;
    },
    openViewModal(Task) {
      this.selectedTask = Task;
      this.showViewModal = true;
    },
    openEditModal(Task) {
      this.selectedTask = Task;
      this.selectedTag = this.optionTags.find(
        (tag) => tag.value === Task.isImportant
      );
      this.showEditTaskModal = true;
    },
    getTypeVariant() {
      return this.isImportant ? "danger" : "info";
    },

    getTagVariant(tag) {
      switch (tag) {
        case "Important":
          return "danger";
        case "Mild":
          return "info";
        default:
          return "secondary";
      }
    },

    async deleteTasks(taskId) {
      this.loading = true;
      await this.removeTaskAction(taskId)
        .then(async (response) => {
          // this.getTasks(this.id);
          this.loading = false;
          this.$swal({
            icon: "success",
            title: "Deleted!",
            text: "The Task has been deleted.",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });

          await this.getTaskByLeadIdAction(this.id);
        })
        .catch((error) => {
          //this.getTasks(this.id);
          this.loading = false;
          this.$swal({
            icon: "error",
            title: "Error",
            text: "The Task has been deleted.",
            customClass: {
              confirmButton: "btn btn-warning",
            },
          });
        });
    },

    // deleting a Task
    confirmDelete(Task) {
      this.$swal({
        title: "Delete Task?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteTasks(Task.id);
        }
      });
    },
  },

  async mounted() {
    await this.getTaskByLeadIdAction(this.id);

    await this.getSalesPersonListAction().then(() => {
      this.SalesPersonList.map((v) =>
        this.optionsSalesPerson.push({
          value: v.id,
          text: `${v.firstName}` + " " + `${v.lastName}`,
        })
      );
    });
    this.optionsSalesPerson = fnSortingDropdown(this.optionsSalesPerson);
  },
  watch: {
    filteredTasks(newTasks) {
      this.totalRows = newTasks.length;
    },
  },
};

</script>
<style lang="scss" scoped>
.is-done {
  color: #666;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>